import { 
  Box,
  Typography
} from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

type Props = {
  dailyStats: {
    date: string;
    revenue: number;
    req: number;
    res: number;
    imp: number;
    clk: number;
  }[];
  totalRevenue: number;
  previousTotalRevenue: number;
  hideSummaryCard?: boolean;
}

export default function AdpopcornDashboard(
  {
    dailyStats,
    totalRevenue,
    previousTotalRevenue,
    hideSummaryCard = false
  }: Props
) {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  
  const revenueDelta = totalRevenue - previousTotalRevenue;
  const revenueDeltaPercentage = revenueDelta > 0 ? '▲' : '▼';

  return (
    <Box sx={{ display: 'flex', gap: 3, mt: 3, minWidth: 0 }}>
      {!hideSummaryCard && (
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            backgroundColor: '#e9edf5',
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            minHeight: 100,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              총 매출
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="#0044D2">
              {formatCurrency(totalRevenue)}
            </Typography>
            <Typography variant="caption" fontSize="0.9rem" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              전월 동기간 대비{' '}
              <Box
                component="span"
                fontSize="0.8rem"
                sx={{
                  color: revenueDelta > 0 ? '#D32F2F' : '#1976D2',
                  display: 'inline-flex',
                  alignItems: 'center',
                  ml: 0.5,
                }}
              >
                {revenueDeltaPercentage}{' '}
                {Math.abs(
                    (revenueDelta / ((previousTotalRevenue ?? 0) || 1)) * 100
                  ).toFixed(0)}
                  %
              </Box>{' '}
              ({revenueDelta > 0 ? '+' : ''}
              {formatCurrency(revenueDelta)})
            </Typography>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          backgroundColor: '#ffffff',
          borderRadius: 2,
          p: 3,
          flex: 1,
          minHeight: 100,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: 'center' }}>
          일별 매출 추이
        </Typography>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={dailyStats}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0044D2" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#0044D2" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="date"
              tickFormatter={(value: string) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <YAxis  tickFormatter={(value: number) => `${(value / 1000).toFixed(0)}K`} />
            <RechartsTooltip
              formatter={(value: number) => formatCurrency(value)}
              labelFormatter={(value: string) =>
                new Date(value).toLocaleDateString('ko-KR')
              }
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#0044D2"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRevenue)"
              name="매출"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
